import React, { Suspense } from 'react';
import clsx from 'clsx';
import { useImage } from 'react-image';

import { makeStyles, createStyles } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';

import ErrorBoundary from './ErrorBoundary';

const useStyles = makeStyles(theme =>
  createStyles({
    root: ({
      objectFit,
      shape,
      border,
    }: Pick<IThubmnailProps, 'objectFit' | 'shape' | 'border'>) => ({
      objectFit: objectFit as any,
      borderRadius:
        shape === 'circle'
          ? '50%'
          : shape === 'square'
          ? 0
          : theme.shape.borderRadius,
      boxShadow: border ? `0 0 0 1px ${theme.palette.divider} inset` : 'none',

      display: 'block',
      pointerEvents: 'none',
      userSelect: 'none',
    }),

    skeleton: ({ shape }: Pick<IThubmnailProps, 'shape'>) => ({
      borderRadius:
        shape === 'circle'
          ? '50%'
          : shape === 'square'
          ? 0
          : theme.shape.borderRadius,
      display: 'block',
    }),
  })
);

export interface IThubmnailProps
  extends React.DetailedHTMLProps<
    React.ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  > {
  imageUrl: string;
  size?: string;

  objectFit?: string;
  shape?: 'roundedRectangle' | 'square' | 'circle';
  border?: boolean;
}

/**
 * Display a thumbnail generated by FT_compressedThumbnail cloud function,
 * falling back to original image if it doesn’t load.
 *
 * Uses react-image: https://github.com/mbrevda/react-image
 */
export function Thubmnail_({
  imageUrl,
  size = '200x200',

  objectFit = 'cover',
  shape = 'roundedRectangle',
  border = false,

  ...props
}: IThubmnailProps) {
  const classes = useStyles({ objectFit, shape, border });

  // Add size suffix just before file name extension (e.g. .jpg)
  const thumbnailUrl = imageUrl.replace(
    /(\.[\w]+\?.*token=[\w-]+$)/,
    `__${size}$1`
  );

  const { src } = useImage({
    srcList: [thumbnailUrl, imageUrl],
  });

  return (
    <img {...props} src={src} className={clsx(classes.root, props.className)} />
  );
}

/**
 * Wrap thumbnail in an ErrorBoundary and Skeleton for loading
 */
export default function Thumbnail(props: IThubmnailProps) {
  const classes = useStyles({ shape: props.shape });

  return (
    <ErrorBoundary
      basic
      message="Failed to load image"
      className={props.className}
    >
      <Suspense
        fallback={
          <Skeleton
            variant="rect"
            className={clsx(classes.skeleton, props.className)}
          />
        }
      >
        <Thubmnail_ {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}
